import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, MapPin, Phone, Heart, Calendar, ExternalLink, Share2, MessageCircle, UserPlus, Trophy, GraduationCap, X, ArrowLeft, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { bannerFor, TYPE_LABEL } from "../components/ListingCard";
import PostCard from "../components/PostCard";

const StatTile = ({ label, value, bg, icon: Icon, testId }) => (
  <div className="nb-stat" style={{ background: bg }} data-testid={testId}>
    <p className="nb-label">{label}</p>
    <p className="font-display text-3xl font-extrabold mt-1 flex items-center gap-2">
      <Icon className="w-5 h-5" /> {value}
    </p>
  </div>
);

export default function InstitutionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [inst, setInst] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);
  const [tab, setTab] = useState(null);
  const [review, setReview] = useState({ teacher_quality: 5, institute_quality: 5, teaching_style: 5, environment: 5, comment: "" });
  const [inquiry, setInquiry] = useState({ name: "", phone: "", message: "" });
  const [showInquiry, setShowInquiry] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/institutions/${id}`).then((r) => {
      setInst(r.data);
    setTab((t) => t || (r.data.type === "coaching" ? "batches" : r.data.type === "school" ? "schoolinfo" : "courses"));
    }).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (user?.role === "student") {
      api.get("/student/dashboard").then((r) => setFollowing(r.data.followed.some((f) => f.id === id))).catch(() => {});
    }
  }, [user, id]);
  useEffect(() => { load(); }, [load]);

  const submitReview = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { ...review, institution_id: id });
      toast.success(data.message);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const sendInquiry = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", { ...inquiry, institution_id: id });
      toast.success(data.message);
      setShowInquiry(false);
      setInquiry({ name: "", phone: "", message: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const toggleFollow = async () => {
    if (!user || user.role !== "student") {
      toast.info("Only registered students can follow. Please login or create a student account.");
      return;
    }
    try {
      const { data } = await api.post(`/follow/${id}`);
      setFollowing(data.following);
      toast.success(data.following ? "Following! You'll see their updates in your dashboard." : "Unfollowed");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sharePost = async (postId) => {
    try {
      const { data } = await api.post(`/posts/${postId}/share`);
      if (data.share_link) await navigator.clipboard.writeText(data.share_link).catch(() => {});
      if (data.points_earned) toast.success(`Link copied! ${data.message}`);
      else toast.info(data.message);
    } catch (err) { toast.error(apiError(err)); }
  };

  const referFriend = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    toast.success("Page link copied — share it with a friend!");
  };

  if (notFound) return (
    <div data-testid="detail-not-found" className="max-w-xl mx-auto px-4 py-24 text-center">
      <p className="font-display text-4xl font-extrabold">Institution not found</p>
      <p className="mt-3 text-slate-500 text-sm">This listing may have been removed or blocked.</p>
      <a href="/institutes" className="nb-btn nb-btn-lime mt-6">Browse Institutes</a>
    </div>
  );
  if (!inst) return <div className="p-16 text-center text-slate-500">Loading...</div>;

  const alreadyReviewed = user?.role === "student" && inst.reviews.some((r) => r.student_id === user.id);
  const tabs = [
    ...(inst.type === "school" ? [{ key: "schoolinfo", label: "School Info" }] : []),
    ...(inst.type === "coaching" ? [{ key: "batches", label: `Batches (${inst.batches?.length || 0})` }] : []),
    { key: "courses", label: `Courses (${inst.courses.length})` },
    { key: "teachers", label: `Teachers (${inst.teachers.length})` },
    { key: "gallery", label: `Gallery (${inst.gallery.length})` },
    { key: "reviews", label: `Reviews (${inst.review_count})` },
    { key: "posts", label: `Posts (${inst.posts?.length || 0})` },
  ];

  const RatingSelect = ({ field, labelText }) => (
    <div>
      <label className="nb-label">{labelText}</label>
      <div className="flex gap-2 mt-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`rating-${field}-${n}`}
            onClick={() => setReview({ ...review, [field]: n })}
            className="w-10 h-10 rounded-full border-2 border-[#171B33] text-sm font-bold transition-colors duration-150"
            style={{ background: review[field] >= n ? "#FCD34D" : "#fff" }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="detail-page max-w-6xl mx-auto px-4 sm:px-8 py-4 md:py-10 pb-24 md:pb-10">
      {/* Profile card */}
      <div className="detail-profile nb overflow-hidden">
        <div className="detail-cover relative h-56 md:h-72 border-b-2 border-[#171B33] overflow-hidden">
          <img src={bannerFor(inst)} alt={inst.name} className="w-full h-full object-cover" />
          <button data-testid="detail-back-btn" onClick={() => window.history.back()} className="md:hidden absolute top-3 left-3 w-9 h-9 rounded-full bg-white border-2 border-[#171B33] flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
          <button data-testid="detail-share-btn" onClick={referFriend} className="md:hidden absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[#171B33] flex items-center justify-center"><Share2 className="w-4 h-4" /></button>
          {inst.badge === "verified" && <span className="absolute bottom-3 left-3 nb-badge" style={{ background: "#A3E635" }}>✓ Verified Institute</span>}
          {inst.gallery?.length > 0 && <span className="absolute bottom-3 right-3 nb-badge bg-white" style={{ fontSize: 10 }}>{inst.gallery.length} photos</span>}
        </div>
        <div className="detail-profile-content p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="detail-summary lg:col-span-7">
            <div className="detail-identity">
            <div className="flex flex-wrap gap-2">
              <span className="nb-badge" style={{ background: "#A3E635" }}>{TYPE_LABEL[inst.type] || "Coaching"}</span>
              {inst.badge === "featured" && <span className="nb-badge" style={{ background: "#FCD34D" }}>★ Featured</span>}
              {inst.badge === "verified" && <span className="nb-badge" style={{ background: "#93C5FD" }}>Verified</span>}
              {inst.since_year && <span className="nb-badge">Since {inst.since_year}</span>}
              <span className="nb-badge" style={{ background: "#FB923C" }}>Rank #{inst.rank || "—"}</span>
            </div>
            <h1 data-testid="detail-name" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4">{inst.name}</h1>
            <p className="mt-2 text-slate-600 flex items-center gap-3 flex-wrap text-sm font-semibold">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {inst.area}, {inst.city}</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {inst.avg_rating || "—"} ({inst.review_count} reviews)</span>
            </p>
            {inst.description && <p className="mt-4 text-sm text-slate-600 leading-relaxed">{inst.description}</p>}
            </div>
            <div className="detail-profile-actions mt-6 flex flex-wrap gap-3">
              <button data-testid="detail-inquiry-btn" onClick={() => setShowInquiry(true)} className="nb-btn nb-btn-lime nb-btn-sm">
                <MessageCircle className="w-4 h-4" /> Send Inquiry
              </button>
              <button data-testid="detail-write-review-btn" onClick={() => setTab("reviews")} className="nb-btn nb-btn-sm">
                <Star className="w-4 h-4" /> Write Review
              </button>
              <button data-testid="detail-refer-btn" onClick={referFriend} className="nb-btn nb-btn-sm">
                <UserPlus className="w-4 h-4" /> Refer a friend
              </button>
              {user?.role !== "institute" && user?.role !== "admin" && (
                <button data-testid="detail-follow-btn" onClick={toggleFollow} className="nb-btn nb-btn-sm"
                  style={following ? { background: "#A3E635" } : {}}>
                  <Heart className="w-4 h-4" style={following ? { fill: "#171B33" } : {}} /> {following ? "Following" : "Follow"} ({inst.followers})
                </button>
              )}
            </div>
          </div>
          <div className="detail-stat-grid lg:col-span-5 grid grid-cols-2 gap-4 self-start">
            <StatTile label="Rank Score" value={Math.round(inst.total_score)} bg="#A3E635" icon={Trophy} testId="detail-score" />
            <StatTile label="Admissions" value={inst.admissions || 0} bg="#FDBA74" icon={GraduationCap} testId="detail-admissions" />
            <StatTile label="Rating" value={inst.avg_rating || "—"} bg="#FCD34D" icon={Star} testId="detail-rating" />
            <StatTile label="Reviews" value={inst.review_count} bg="#93C5FD" icon={MessageCircle} testId="detail-reviews-count" />
          </div>
        </div>
      </div>

      {/* Contact bar */}
      <div className="detail-contact nb mt-6 px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 text-sm font-semibold">
        {inst.phone && <a href={`tel:${inst.phone}`} data-testid="detail-phone" className="flex items-center gap-2 hover:text-lime-600 transition-colors duration-150"><Phone className="w-4 h-4" /> {inst.phone}</a>}
        {inst.whatsapp && <a href={`https://wa.me/${inst.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="detail-whatsapp" className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors duration-150"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
        {Object.entries(inst.social_links || {}).filter(([, v]) => v).map(([k, v]) => (
          <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" data-testid={`detail-social-${k}`} className="flex items-center gap-2 capitalize hover:text-lime-600 transition-colors duration-150"><ExternalLink className="w-4 h-4" /> {k}</a>
        ))}
        <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {inst.area}, {inst.city}</span>
      </div>

      {/* Tabs */}
      <div className="detail-tabs nb-flat mt-8 inline-flex flex-wrap gap-1 p-1.5" style={{ borderRadius: 9999 }}>
        {tabs.map((t) => (
          <button key={t.key} data-testid={`detail-tab-${t.key}`} onClick={() => setTab(t.key)}
            className="px-4 py-2 rounded-full text-sm font-bold transition-colors duration-150"
            style={tab === t.key ? { background: "#171B33", color: "#fff" } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="detail-content mt-8">
        {tab === "schoolinfo" && (() => {
          const si = inst.school_info || {};
          const infoRows = [
            ["Board", si.board], ["Medium", si.medium], ["Classes", si.classes], ["Gender", si.gender],
            ["School Type", si.school_type], ["No. of Classrooms", si.classrooms], ["School Area", si.area_sqm],
            ["Parent Institution", si.parent_institution], ["Website", si.website],
          ].filter(([, v]) => v);
          const hasAny = infoRows.length || si.about || (si.facilities || []).length || (si.fees || []).length;
          return !hasAny ? <p className="text-sm text-slate-500">School information not added yet.</p> : (
            <div className="space-y-8">
              {si.about && (
                <div className="nb p-6 md:p-8">
                  <h2 className="font-display text-xl font-extrabold">About {inst.name}</h2>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-line">{si.about}</p>
                </div>
              )}
              {infoRows.length > 0 && (
                <div className="nb p-6 md:p-8">
                  <h2 className="font-display text-xl font-extrabold">School Information</h2>
                  <div className="school-info-grid mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {infoRows.map(([k, v]) => (
                      <div key={k} className="nb-flat p-4" style={{ borderRadius: 12 }}>
                        <p className="nb-label">{k}</p>
                        <p className="font-bold text-sm mt-1 break-words">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(si.facilities || []).length > 0 && (
                <div className="nb p-6 md:p-8">
                  <h2 className="font-display text-xl font-extrabold">Facilities & Activities</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {si.facilities.map((f) => <span key={f} className="nb-badge" style={{ background: "#F2FBE3" }}>{f}</span>)}
                  </div>
                </div>
              )}
              {(si.fees || []).filter((f) => f.type).length > 0 && (
                <div className="nb overflow-x-auto">
                  <div className="p-6 pb-0"><h2 className="font-display text-xl font-extrabold">Fee Details</h2></div>
                  <table className="w-full text-sm mt-4">
                    <thead><tr className="border-b-2 border-[#171B33] text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="p-4">Fee Type</th><th className="p-4">Frequency</th><th className="p-4">Amount</th>
                    </tr></thead>
                    <tbody>
                      {si.fees.filter((f) => f.type).map((f) => (
                        <tr key={`${f.type}-${f.frequency}-${f.amount}`} className="border-b border-slate-200 last:border-0">
                          <td className="p-4 font-semibold">{f.type}</td>
                          <td className="p-4">{f.frequency}</td>
                          <td className="p-4 font-display font-bold">₹{f.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {tab === "batches" && (
          (inst.batches || []).length === 0 ? <p className="text-sm text-slate-500">No batches announced yet.</p> : (
            <div className="space-y-8">
              {["ongoing", "upcoming"].map((status) => {
                const list = inst.batches.filter((b) => b.status === status);
                if (!list.length) return null;
                return (
                  <div key={status}>
                    <h2 className="font-display text-xl font-extrabold capitalize flex items-center gap-2">
                      {status} Batches
                      <span className="nb-badge" style={{ background: status === "ongoing" ? "#A3E635" : "#FCD34D" }}>{list.length}</span>
                    </h2>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {list.map((b) => (
                        <div key={b.id} data-testid={`batch-card-${b.id}`} className="nb p-6">
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-display text-lg font-extrabold">{b.name}</p>
                            <span className="nb-badge" style={{ background: status === "ongoing" ? "#A3E635" : "#FCD34D" }}>{status}</span>
                          </div>
                          <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                            {b.timings && <p><span className="font-bold">Timings:</span> {b.timings}</p>}
                            {b.start_date && <p><span className="font-bold">Starts:</span> {b.start_date}</p>}
                            {b.pricing && <p><span className="font-bold">Pricing:</span> ₹{b.pricing}</p>}
                            {(b.teachers || []).length > 0 && <p><span className="font-bold">Teachers:</span> {b.teachers.join(", ")}</p>}
                            {b.description && <p className="text-slate-500">{b.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === "courses" && (
          inst.courses.length === 0 ? <p className="text-sm text-slate-500">No courses listed yet.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inst.courses.map((c) => (
                <div key={c.id} data-testid={`course-card-${c.id}`} className="nb p-6">
                  <p className="nb-label">Course</p>
                  <p className="font-display text-lg font-extrabold mt-1">{c.title}</p>
                  <p className="text-sm font-semibold text-slate-600 mt-2">{c.fee && `₹${c.fee}`} {c.period && <span className="ml-2">📅 {c.period}</span>}</p>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "teachers" && (
          inst.teachers.length === 0 ? <p className="text-sm text-slate-500">No teachers listed yet.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inst.teachers.map((t) => (
                <div key={t.id} data-testid={`teacher-card-${t.id}`} className="nb p-6 flex gap-4">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} className="w-16 h-16 rounded-xl object-cover border-2 border-[#171B33] flex-shrink-0" />
                  ) : (
                    <span className="w-16 h-16 rounded-xl border-2 border-[#171B33] flex items-center justify-center font-display font-extrabold text-xl flex-shrink-0" style={{ background: "#A3E635" }}>
                      {t.name?.[0] || "T"}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="font-display text-lg font-extrabold">{t.name}</p>
                    {t.subject && <p className="text-sm font-semibold text-slate-600">{t.subject}</p>}
                    {t.experience && <p className="text-xs text-slate-500 mt-1">Experience: {t.experience}</p>}
                    {t.detail && <p className="text-sm text-slate-600 mt-1">{t.detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "gallery" && (
          inst.gallery.length === 0 ? <p className="text-sm text-slate-500">No gallery images yet.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {inst.gallery.map((g) => (
                <img key={g.id} src={g.image_url} alt="gallery" className="nb-flat w-full h-48 object-cover" style={{ borderRadius: 14 }} />
              ))}
            </div>
          )
        )}

        {tab === "reviews" && (
          <div className="space-y-6">
            {user?.role === "student" && !alreadyReviewed && (
              <div className="nb p-6 md:p-8">
                <h2 className="font-display text-xl font-extrabold">Write a Review <span className="text-sm font-sans font-bold text-lime-600">(+10 points)</span></h2>
                <form onSubmit={submitReview} className="mt-5 space-y-5">
                  <RatingSelect field="teacher_quality" labelText="Teachers" />
                  <RatingSelect field="institute_quality" labelText="The Institute" />
                  <RatingSelect field="teaching_style" labelText="Teaching Style" />
                  <RatingSelect field="environment" labelText="Environment" />
                  <div>
                    <label className="nb-label">Comment</label>
                    <textarea data-testid="review-comment-input" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} rows={3} className="nb-input mt-1.5" />
                  </div>
                  <button data-testid="review-submit-btn" disabled={busy} className="nb-btn nb-btn-lime">{busy ? "Submitting..." : "Submit Review"}</button>
                </form>
              </div>
            )}
            {!user && <p className="text-sm text-slate-500">Login as a student to write a review and earn points.</p>}
            {alreadyReviewed && <p data-testid="already-reviewed-note" className="text-sm font-semibold text-lime-700">You've already reviewed this institution. Thanks!</p>}
            {inst.reviews.length === 0 ? <p className="text-sm text-slate-500">No reviews yet. Be the first!</p> : inst.reviews.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id}`} className="nb p-5">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-sm flex items-center gap-2">{r.student_name}
                    {r.genuine && <span data-testid={`genuine-badge-${r.id}`} className="nb-badge" style={{ background: "#A3E635" }}>✓ Genuine</span>}
                  </p>
                  <span className="nb-badge" style={{ background: "#FCD34D" }}>
                    <Star className="w-3 h-3" /> {((r.teacher_quality + (r.institute_quality ?? r.teacher_quality) + (r.teaching_style ?? 5) + (r.environment ?? 5)) / 4).toFixed(1)}
                  </span>
                </div>
                <div className="flex gap-4 text-xs text-slate-500 mt-2 flex-wrap">
                  <span>Teachers: {r.teacher_quality}/5</span>
                  <span>Institute: {r.institute_quality ?? "—"}/5</span>
                  <span>Style: {r.teaching_style ?? "—"}/5</span>
                  <span>Environment: {r.environment ?? "—"}/5</span>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "posts" && (
          (inst.posts || []).length === 0 ? <p className="text-sm text-slate-500">No updates published yet.</p> : (
            <div className="space-y-4">
              {inst.posts.map((p) => <PostCard key={p.id} post={p} user={user} onShare={sharePost} />)}
            </div>
          )
        )}
      </div>

      {/* Mobile sticky actions */}
      <div data-testid="detail-mobile-actions" className="md:hidden fixed left-0 right-0 z-40 px-3 py-2 grid grid-cols-2 gap-2 bg-white border-t-2 border-[#171B33]" style={{ bottom: "calc(64px + env(safe-area-inset-bottom))" }}>
        <button data-testid="detail-mobile-enquire-btn" onClick={() => setShowInquiry(true)} className="nb-btn nb-btn-sm justify-center" style={{ background: "#F2FBE3" }}><Send className="w-4 h-4" /> Enquire Now</button>
        {inst.whatsapp
          ? <a href={`https://wa.me/${inst.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi, I want to apply at ${inst.name}`)}`} target="_blank" rel="noreferrer" data-testid="detail-mobile-apply-btn" className="nb-btn nb-btn-lime nb-btn-sm justify-center"><MessageCircle className="w-4 h-4" /> Apply Now</a>
          : <button data-testid="detail-mobile-apply-btn" onClick={() => setShowInquiry(true)} className="nb-btn nb-btn-lime nb-btn-sm justify-center"><GraduationCap className="w-4 h-4" /> Apply Now</button>}
      </div>

      {/* Inquiry modal */}
      {showInquiry && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowInquiry(false)}>
          <div className="nb bg-white p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold">Send Inquiry</h2>
              <button data-testid="inquiry-close-btn" onClick={() => setShowInquiry(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={sendInquiry} className="mt-5 space-y-4">
              <div><label className="nb-label">Your Name</label>
                <input data-testid="inquiry-name-input" required value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Phone</label>
                <input data-testid="inquiry-phone-input" required value={inquiry.phone} onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Message</label>
                <textarea data-testid="inquiry-message-input" rows={3} value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} className="nb-input mt-1.5" /></div>
              <button data-testid="inquiry-submit-btn" disabled={busy} className="nb-btn nb-btn-lime w-full">{busy ? "Sending..." : "Send Inquiry"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
